"use client";

import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
// launcher-core
import { determineLaunchUrl } from "../../core/load-client-window";
// launcher-components
import { ChatButton } from "../ChatButton";
import { IFrameChatWindow } from "../IFrameChatWindow";
import FixedPositionWrapper from "../FixedPositionWrapper";
// @nib-group/chatbot-shared
import { ClientContextType } from "@nib-group/chatbot-shared/types";
import { AnimateFadeOut, AnimateFadeDirectionIn } from "@nib-group/chatbot-shared/styles";
import { getCachedSessionId, getCachedInlineChatMinimised, getCachedReceivedMessages } from "@nib-group/chatbot-shared/local-storage";
import { MSG_KEYS, listenForPostMessage } from "@nib-group/chatbot-shared/post-message";
// misc
import CONFIG from "./ChatLauncher.config";
import { checkForActivePopOutChat, checkActiveChatRecency } from "./methods";
export const ChatLauncher = _ref => {
  let {
    path = "/chat",
    config
  } = _ref;
  var _a, _b;
  const [canRenderChatIframe, setCanRenderChatIframe] = useState(false);
  const [hasActivePopOut, setHasActivePopOut] = useState(false);
  const [hasExistingSession, setHasExistingSession] = useState(false);
  const [shouldStartMinimised, setShouldStartMinimised] = useState(false);
  const [chatUrl, setChatUrl] = useState("");
  // ----------------------------------------
  // Inference variables
  const hasWindow = typeof window !== "undefined";
  // ----------------------------------------
  // Effects
  // Necessary to safeguard against server-side
  // rendering errors when the `window` doesn't exist.
  useEffect(() => {
    if (!hasWindow) return;
    setChatUrl(determineLaunchUrl(path, config === null || config === void 0 ? void 0 : config.clientPath));
  }, [hasWindow, config, path]);
  // Resume chat, and coordinate `localStorage` caching parity with chat client
  useEffect(() => {
    var _a;
    const hasCachedSessionId = !!getCachedSessionId();
    // We can infer engagement from the message received amount
    const hasEngagedWithExistingChat = ((_a = parseInt(getCachedReceivedMessages())) !== null && _a !== void 0 ? _a : 0) > 1;
    const hasSufficientlyRecentActiveChat = checkActiveChatRecency();
    const hasActivePopOutResult = checkForActivePopOutChat();
    const cachedMinimisedValue = getCachedInlineChatMinimised();
    const didHaveInlineChatMinimised = cachedMinimisedValue === "true";
    // There are a number of checks to discern whether it is worth
    // the attempt to resume a cached chat session:
    // - If there is a cached session ID
    // - and the corresponding timestamp is recent enough
    // - and there doesn't seem to already be a pop-out chat window active
    // 		- (we don't want to show the inline chat if there's an active pop-out)
    // - and the user has engaged with the chat window
    const shouldAttemptChatResume = hasCachedSessionId && hasSufficientlyRecentActiveChat && !hasActivePopOutResult && (
    // Note that it's acceptable for `cachedMinimisedValue` to have ANY
    // value, because that indicates that they've interacted with
    // the inline chat, and that's sufficient evidence to persist
    hasEngagedWithExistingChat || !!cachedMinimisedValue);
    setHasActivePopOut(hasActivePopOutResult);
    setHasExistingSession(shouldAttemptChatResume);
    if (shouldAttemptChatResume) {
      if (didHaveInlineChatMinimised) {
        setShouldStartMinimised(didHaveInlineChatMinimised);
      }
      // If so, we can immediately show the chat iframe and it'll
      // connect to the existing session via logic in `InitChatContext`
      setCanRenderChatIframe(true);
    }
    // Establish event listener to keep `localStorage` parity
    // cross-window (cross-origin on `localhost`)
    listenForPostMessage({
      msgKey: MSG_KEYS.CROSS_WINDOW_LOCALSTORAGE,
      onSuccess: function (_) {
        let payload = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
        const {
          key,
          value
        } = payload;
        if (!value) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, value);
        }
      }
    });
  }, []);
  const resetChatState = () => {
    setCanRenderChatIframe(false);
    setHasActivePopOut(false);
    setHasExistingSession(false);
    setShouldStartMinimised(false);
  };
  // ----------------------------------------
  // Render variables
  const canRender = hasWindow && !!chatUrl && !hasActivePopOut;
  const impliedChannel = canRender && CONFIG.OS_HOSTNAMES.includes(window.location.hostname) ? "os" : undefined;
  if (!canRender) {
    return null;
  }
  return _jsxs(_Fragment, {
    children: [_jsx(FixedPositionWrapper, {
      bottom: {
        tablet: "40px",
        mobile: "20px"
      },
      right: {
        tablet: "40px",
        mobile: "20px"
      },
      children: _jsx(AnimateFadeOut, {
        activate: canRenderChatIframe,
        unmountWhen: true,
        children: _jsx(ChatButton, {
          id: CONFIG.TARGETING_ID,
          onClick: () => {
            setCanRenderChatIframe(true);
          }
        })
      })
    }), _jsx(FixedPositionWrapper, {
      bottom: "0",
      right: {
        mobile: "20px",
        tablet: "40px"
      },
      children: _jsx(AnimateFadeDirectionIn, {
        activate: canRenderChatIframe,
        unmountWhen: false,
        animY: {
          in: "0",
          out: "100px"
        },
        children: _jsx(IFrameChatWindow, {
          url: chatUrl,
          chatConfig: Object.assign(Object.assign({
            clientContext: ClientContextType.Inline
          }, config), {
            channel: (_a = config === null || config === void 0 ? void 0 : config.channel) !== null && _a !== void 0 ? _a : impliedChannel,
            started_on_page: (_b = config === null || config === void 0 ? void 0 : config.started_on_page) !== null && _b !== void 0 ? _b : window.location.pathname
          }),
          isResuming: hasExistingSession,
          startMinimised: shouldStartMinimised,
          resetChatState: resetChatState
        })
      })
    })]
  });
};